import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import TextInput from "../../components/Select/InputText";
import TableLoadingRow from "../../components/TableLoadingRow";
import { useAuth } from "../../context/AuthContext";
import { getCoachesService } from "../../services/coachService";
import { getSalaryReport, markSalaryPaid, SalaryRow, SalaryTotals } from "../../services/salaryService";
import { showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";
import { FEATURE_KEYS, hasFeature } from "../../utils/featureAccess";

type OptionItem = {
    value: string;
    label: string;
};

const emptyTotals: SalaryTotals = {
    sessionsCount: 0,
    payableHours: 0,
    sessionRevenue: 0,
    subscriptionRevenueBasis: 0,
    salary: 0,
    paidSalary: 0,
    outstandingSalary: 0,
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const toArray = (value: unknown): unknown[] => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") {
        const data = value as { data?: unknown; items?: unknown; coaches?: unknown };
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.coaches)) return data.coaches;
    }
    return [];
};

const buildName = (entry: Record<string, unknown>): string => {
    const first = asString(entry.firstName);
    const last = asString(entry.lastName);
    const full = `${first} ${last}`.trim();
    return full || asString(entry.name) || "-";
};

const formatMoney = (value: number) => (
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
);

const formatNumber = (value: number) => (
    Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
);

const formatDate = (value: string) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const formatMethods = (row: SalaryRow) => {
    const components = row.enabledComponents || {
        fixed: row.salaryType === "fixed",
        hourly: row.salaryType === "hourly",
        percentage: row.salaryType === "percentage",
        session: false,
    };
    const labels = [
        components.fixed ? "Fixed" : "",
        components.hourly ? "Hourly" : "",
        components.percentage ? "Percentage" : "",
        components.session ? "Per Session" : "",
    ].filter(Boolean);

    return labels.length ? labels.join(" + ") : "None";
};

const getComponent = (row: SalaryRow, key: keyof SalaryRow["salaryComponents"]) => (
    Number(row.salaryComponents?.[key] || 0)
);

const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    const formatDate = (date: Date) => {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    return {
        startDate: formatDate(start),
        endDate: formatDate(end),
    };
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string; error?: string } } };
    return errObj.response?.data?.message ?? errObj.response?.data?.error ?? fallback;
};

const SalaryPage = () => {
    const { user } = useAuth();
    const initialRange = useMemo(() => getCurrentMonthRange(), []);
    const [rows, setRows] = useState<SalaryRow[]>([]);
    const [totals, setTotals] = useState<SalaryTotals>(emptyTotals);
    const [coaches, setCoaches] = useState<OptionItem[]>([]);
    const [coachId, setCoachId] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(false);
    const [payingCoachId, setPayingCoachId] = useState("");
    const [error, setError] = useState("");
    const canPaySalary = hasFeature(FEATURE_KEYS.ACCOUNTING)
        && hasPermission(user, "salary.write")
        && hasPermission(user, "accounting.write");
    const componentTotals = useMemo(() => rows.reduce((result, row) => ({
        fixed: result.fixed + getComponent(row, "fixed"),
        hourly: result.hourly + getComponent(row, "hourly"),
        percentage: result.percentage + getComponent(row, "percentage"),
        session: result.session + getComponent(row, "session"),
    }), { fixed: 0, hourly: 0, percentage: 0, session: 0 }), [rows]);

    const fetchCoaches = useCallback(async () => {
        if (!user?.id) return;

        try {
            const response = await getCoachesService();
            const options = toArray(response)
                .map((entry) => {
                    const coach = asRecord(entry);
                    return {
                        value: asString(coach._id) || asString(coach.id),
                        label: buildName(coach),
                    };
                })
                .filter((item) => item.value);
            setCoaches(options);
        } catch (err) {
            console.error("Failed to load coaches", err);
            setError("Failed to load coach options");
        }
    }, [user?.id]);

    const fetchSalary = useCallback(async () => {
        if (!user?.id) return;

        setLoading(true);
        setError("");
        try {
            const response = await getSalaryReport({ coachId, startDate, endDate });
            setRows(response.rows || []);
            setTotals(response.totals || emptyTotals);
        } catch (err: unknown) {
            console.error("Failed to calculate salary", err);
            setError(readErrorMessage(err, "Failed to calculate salary"));
        } finally {
            setLoading(false);
        }
    }, [user?.id, coachId, startDate, endDate]);

    useEffect(() => {
        fetchCoaches();
    }, [fetchCoaches]);

    useEffect(() => {
        fetchSalary();
    }, [fetchSalary]);

    const handleMarkPaid = async (row: SalaryRow) => {
        if (!startDate || !endDate || !paymentDate || row.payment) return;
        if (!window.confirm(`Record ${formatMoney(row.salary)} as paid to ${row.coachName}? This will create a salary expense.`)) return;

        setPayingCoachId(row.coachId);
        setError("");
        try {
            const result = await markSalaryPaid({
                coachId: row.coachId,
                startDate,
                endDate,
                paymentDate,
            });
            showSuccessToast(result.alreadyPaid ? "Salary was already recorded as paid" : "Salary paid and added to expenses");
            await fetchSalary();
        } catch (err: unknown) {
            setError(readErrorMessage(err, "Failed to record salary payment"));
        } finally {
            setPayingCoachId("");
        }
    };

    return (
        <div className="w-full p-6">
            <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Salary Computation</h1>
                    <p className="text-sm text-gray-500">Monthly coach salary report from fixed, hourly, percentage, and per-session rules.</p>
                </div>
                <button
                    type="button"
                    onClick={fetchSalary}
                    disabled={loading}
                    className="w-fit rounded-md bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                    {loading ? "Calculating..." : "Refresh"}
                </button>
            </div>

            {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-5">
                <div className="flex flex-col gap-1">
                    <label className="text-sm font-semibold">Coach</label>
                    <select
                        value={coachId}
                        onChange={(event) => setCoachId(event.target.value)}
                        className="rounded border px-3 py-2"
                    >
                        <option value="">All Coaches</option>
                        {coaches.map((coach) => (
                            <option key={coach.value} value={coach.value}>{coach.label}</option>
                        ))}
                    </select>
                </div>
                <TextInput label="Start Date" type="date" name="startDate" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                <TextInput label="End Date" type="date" name="endDate" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                {canPaySalary && <TextInput label="Payment Date" type="date" name="paymentDate" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />}
                <div className="flex items-end">
                    <button
                        type="button"
                        onClick={() => {
                            setCoachId("");
                            setStartDate(initialRange.startDate);
                            setEndDate(initialRange.endDate);
                        }}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Sessions</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : totals.sessionsCount.toLocaleString()}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Payable Hours</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : formatNumber(totals.payableHours)}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">PT Revenue Basis</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : formatMoney(totals.sessionRevenue)}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Subscription Revenue Basis</p>
                    <p className="mt-2 text-2xl font-bold">{loading ? "..." : formatMoney(totals.subscriptionRevenueBasis)}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Computed Salary</p>
                    <p className="mt-2 text-2xl font-bold text-[#ac120c]">{loading ? "..." : formatMoney(totals.salary)}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Paid Salary</p>
                    <p className="mt-2 text-2xl font-bold text-green-600">{loading ? "..." : formatMoney(totals.paidSalary)}</p>
                </div>
                <div className="rounded-md border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold uppercase text-gray-500">Outstanding</p>
                    <p className="mt-2 text-2xl font-bold text-amber-600">{loading ? "..." : formatMoney(totals.outstandingSalary)}</p>
                </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-black text-white">
                        <tr>
                            <th className="p-3 text-left">Coach</th>
                            <th className="p-3 text-left">Methods</th>
                            <th className="p-3 text-right">Sessions</th>
                            <th className="p-3 text-right">Hours</th>
                            <th className="p-3 text-right">PT Revenue Basis</th>
                            <th className="p-3 text-right">Subscription Basis</th>
                            <th className="p-3 text-right">Fixed</th>
                            <th className="p-3 text-right">Hourly</th>
                            <th className="p-3 text-right">Percentage</th>
                            <th className="p-3 text-right">Per Session</th>
                            <th className="p-3 text-right">Salary</th>
                            <th className="p-3 text-right">Payment</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <TableLoadingRow colSpan={12} label="Calculating salary..." />
                        ) : rows.length ? (
                            rows.map((row) => (
                                <Fragment key={row.coachId}>
                                    <tr className="border-t border-gray-200 hover:bg-gray-50">
                                        <td className="p-3 font-medium">{row.coachName}</td>
                                        <td className="p-3">{formatMethods(row)}</td>
                                        <td className="p-3 text-right">{row.sessionsCount.toLocaleString()}</td>
                                        <td className="p-3 text-right">{formatNumber(row.payableHours)}</td>
                                        <td className="p-3 text-right">{formatMoney(row.sessionRevenue)}</td>
                                        <td className="p-3 text-right">{formatMoney(row.subscriptionRevenueBasis)}</td>
                                        <td className="p-3 text-right">{formatMoney(getComponent(row, "fixed"))}</td>
                                        <td className="p-3 text-right">{formatMoney(getComponent(row, "hourly"))}</td>
                                        <td className="p-3 text-right">{formatMoney(getComponent(row, "percentage"))}</td>
                                        <td className="p-3 text-right">{formatMoney(getComponent(row, "session"))}</td>
                                        <td className="p-3 text-right font-semibold">{formatMoney(row.salary)}</td>
                                        <td className="p-3 text-right">
                                            {row.payment ? (
                                                <div className="text-xs">
                                                    <span className="font-semibold text-green-700">Paid {formatMoney(row.payment.amount)}</span>
                                                    <span className="block text-gray-500">{formatDate(row.payment.paidAt)}</span>
                                                </div>
                                            ) : canPaySalary ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleMarkPaid(row)}
                                                    disabled={payingCoachId === row.coachId || row.salary <= 0 || !startDate || !endDate}
                                                    className="rounded-md bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                                >
                                                    {payingCoachId === row.coachId ? "Saving..." : "Mark Paid"}
                                                </button>
                                            ) : (
                                                <span className="text-xs text-gray-500">Unpaid</span>
                                            )}
                                        </td>
                                    </tr>
                                    {row.commissionDetails?.length > 0 && (
                                        <tr className="border-t border-gray-100 bg-gray-50/70">
                                            <td colSpan={12} className="px-3 py-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    PT package commission details
                                                </p>
                                                <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                                                    <table className="w-full text-xs">
                                                        <thead className="bg-gray-100 text-gray-600">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left">Package</th>
                                                                <th className="px-3 py-2 text-right">Sessions</th>
                                                                <th className="px-3 py-2 text-right">Revenue Basis</th>
                                                                <th className="px-3 py-2 text-right">Commission %</th>
                                                                <th className="px-3 py-2 text-right">Commission</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.commissionDetails.map((detail) => (
                                                                <tr key={detail.packageId} className="border-t border-gray-100">
                                                                    <td className="px-3 py-2 font-medium">{detail.packageName}</td>
                                                                    <td className="px-3 py-2 text-right">{detail.sessionsCount.toLocaleString()}</td>
                                                                    <td className="px-3 py-2 text-right">{formatMoney(detail.revenueBasis)}</td>
                                                                    <td className="px-3 py-2 text-right">{formatNumber(detail.percentage)}%</td>
                                                                    <td className="px-3 py-2 text-right font-semibold">{formatMoney(detail.commissionAmount)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {row.subscriptionCommissionDetails?.length > 0 && (
                                        <tr className="border-t border-gray-100 bg-emerald-50/40">
                                            <td colSpan={12} className="px-3 py-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    Normal subscription commission details
                                                </p>
                                                <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                                                    <table className="w-full text-xs">
                                                        <thead className="bg-gray-100 text-gray-600">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left">Member</th>
                                                                <th className="px-3 py-2 text-left">Date</th>
                                                                <th className="px-3 py-2 text-left">Package</th>
                                                                <th className="px-3 py-2 text-left">Activity</th>
                                                                <th className="px-3 py-2 text-right">Package Price</th>
                                                                <th className="px-3 py-2 text-right">Allocation</th>
                                                                <th className="px-3 py-2 text-right">Allocated Revenue</th>
                                                                <th className="px-3 py-2 text-right">Commission %</th>
                                                                <th className="px-3 py-2 text-right">Coach Salary</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.subscriptionCommissionDetails.map((detail) => (
                                                                <tr key={`${detail.subscriptionId}-${detail.activityName}`} className="border-t border-gray-100">
                                                                    <td className="px-3 py-2 font-medium">{detail.memberName}</td>
                                                                    <td className="px-3 py-2">{formatDate(detail.subscriptionDate)}</td>
                                                                    <td className="px-3 py-2">{detail.packageName}</td>
                                                                    <td className="px-3 py-2 font-medium">{detail.activityName}</td>
                                                                    <td className="px-3 py-2 text-right">{formatMoney(detail.subscriptionPrice)}</td>
                                                                    <td className="px-3 py-2 text-right">{formatNumber(detail.allocationPercentage)}%</td>
                                                                    <td className="px-3 py-2 text-right">{formatMoney(detail.allocatedAmount)}</td>
                                                                    <td className="px-3 py-2 text-right">{formatNumber(detail.commissionPercentage)}%</td>
                                                                    <td className="px-3 py-2 text-right font-semibold">{formatMoney(detail.commissionAmount)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {row.salarySessionEnabled && row.sessionDetails && (
                                        <tr className="border-t border-gray-100 bg-blue-50/40">
                                            <td colSpan={12} className="px-3 py-3">
                                                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    Per-session salary details
                                                </p>
                                                <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                                                    <table className="w-full text-xs">
                                                        <thead className="bg-gray-100 text-gray-600">
                                                            <tr>
                                                                <th className="px-3 py-2 text-left">Session Source</th>
                                                                <th className="px-3 py-2 text-right">Sessions</th>
                                                                <th className="px-3 py-2 text-right">Client Revenue</th>
                                                                <th className="px-3 py-2 text-right">Payment</th>
                                                                <th className="px-3 py-2 text-right">Coach Rate</th>
                                                                <th className="px-3 py-2 text-right">Coach Pay</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr className="border-t border-gray-100">
                                                                <td className="px-3 py-2 font-medium">Package</td>
                                                                <td className="px-3 py-2 text-right">{row.sessionDetails.packageSessionsCount.toLocaleString()}</td>
                                                                <td className="px-3 py-2 text-right">-</td>
                                                                <td className="px-3 py-2 text-right">Included in package</td>
                                                                <td className="px-3 py-2 text-right">{formatMoney(row.sessionDetails.ratePerSession)}</td>
                                                                <td className="px-3 py-2 text-right font-semibold">{formatMoney(row.sessionDetails.packageCoachPay)}</td>
                                                            </tr>
                                                            <tr className="border-t border-gray-100">
                                                                <td className="px-3 py-2 font-medium">Pay Per Use</td>
                                                                <td className="px-3 py-2 text-right">{row.sessionDetails.payPerUseSessionsCount.toLocaleString()}</td>
                                                                <td className="px-3 py-2 text-right">{formatMoney(row.sessionDetails.payPerUseRevenue)}</td>
                                                                <td className="px-3 py-2 text-right">
                                                                    {row.sessionDetails.paidPayPerUseSessionsCount} paid / {row.sessionDetails.unpaidPayPerUseSessionsCount} unpaid
                                                                    {row.sessionDetails.outstandingPayPerUseRevenue > 0 && (
                                                                        <span className="block text-amber-700">
                                                                            {formatMoney(row.sessionDetails.outstandingPayPerUseRevenue)} outstanding
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-2 text-right">{formatMoney(row.sessionDetails.ratePerSession)}</td>
                                                                <td className="px-3 py-2 text-right font-semibold">{formatMoney(row.sessionDetails.payPerUseCoachPay)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={12} className="p-4 text-center text-gray-500">No coaches found.</td>
                            </tr>
                        )}
                        {!loading && rows.length > 0 && (
                            <tr className="border-t border-gray-300 bg-gray-50 font-semibold">
                                <td className="p-3" colSpan={6}>Totals</td>
                                <td className="p-3 text-right">{formatMoney(componentTotals.fixed)}</td>
                                <td className="p-3 text-right">{formatMoney(componentTotals.hourly)}</td>
                                <td className="p-3 text-right">{formatMoney(componentTotals.percentage)}</td>
                                <td className="p-3 text-right">{formatMoney(componentTotals.session)}</td>
                                <td className="p-3 text-right">{formatMoney(totals.salary)}</td>
                                <td className="p-3 text-right">{formatMoney(totals.paidSalary)} paid</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SalaryPage;
